// hooks/useOrders.ts
import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { PaginationData } from "../types/orderTypes";
import api from "src/features/auth/axiosConfig";
import { useUser } from "src/shared/context/UserContext";
import { Order } from "src/shared/services/interfacev2";
import { getOrder } from "src/shared/services/servicesv2";

export const useOrders = () => {
  const [orderList, setOrderList] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { userConfigration } = useUser();
  const [paginationData, setPaginationData] = useState<PaginationData>({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 10,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState("Today");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedDate2, setSelectedDate2] = useState("");
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  const fetchOrderDetails = useCallback(
    async (pageNumber: number, pageSize: number, search: string, filterOption: string) => {
      try {
        setLoading(true);
        setError(null);

        // For CustomizeDate, ensure both dates are selected
        if (filterOption === "CustomizeDate" && (!selectedDate || !selectedDate2)) {
          setOrderList([]);
          setPaginationData({ totalRecords: 0, totalPages: 0, currentPage: 1, pageSize });
          return;
        }

        const params: Record<string, string | number> = {
          pageNumber,
          pageSize,
          dateFilter: (() => {
            const normalized = filterOption.replace(/\s+/g, "").toLowerCase();

            if (normalized === "lastweek") return "7";
            if (normalized === "lastmonth") return "30";
            if (normalized === "180days") return "180";
            if (normalized === "customizedate") return "custom";

            return normalized; // today, yesterday, etc.
          })(),
          searchBy: search,
        };

        // Add startDate and endDate if CustomizeDate
        if (filterOption === "CustomizeDate") {
          params.startDate = selectedDate;
          params.endDate = selectedDate2;
        }
        const response = await getOrder(
          "", // id
          pageNumber,
          pageSize,
          "", // from
          "", // to
          "", // customer name
          "", // staffId
          "", // sortField
          "", // status
          // { searchBy: search }
          params,
        );

        if (response.data?.result) {
          setOrderList(response.data.result);
          setPaginationData({
            totalRecords: response.data.totalRecords || 0,
            totalPages: response.data.totalPages || 0,
            currentPage: response.data.currentPage || 1,
            pageSize: response.data.pageSize || pageSize,
          });
        } else {
          setOrderList([]);
          toast.info("No orders found.");
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
        setError("Failed to fetch orders");
        toast.error("Failed to fetch orders");
      } finally {
        setLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [searchTerm, selectedOption, selectedDate, selectedDate2],
  );

  const handleSearch = useCallback(
    (value: string) => {
      const cleanValue = value.replace(/[!@#$%^&?*<>|/{}():_+=";'.,[\]`-]/g, "");
      setSearchTerm(cleanValue);

      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      searchTimeout.current = setTimeout(() => {
        fetchOrderDetails(1, paginationData.pageSize, cleanValue, selectedOption);
      }, 500);
    },
    [fetchOrderDetails, paginationData.pageSize, selectedOption],
  );

  const handleFilterChange = useCallback(
    (value: string) => {
      setSelectedOption(value);

      if (value !== "CustomizeDate") {
        setSelectedDate("");
        setSelectedDate2("");
        fetchOrderDetails(1, paginationData.pageSize, searchTerm, value);
      } else {
        setOrderList([]);
        setPaginationData({
          totalRecords: 0,
          totalPages: 0,
          currentPage: 1,
          pageSize: paginationData.pageSize,
        });
      }
    },
    [fetchOrderDetails, paginationData.pageSize, searchTerm],
  );

  const applyCustomDateFilter = useCallback(() => {
    if (!selectedDate || !selectedDate2) {
      toast.warning("Please select both start and end dates");
      return;
    }
    if (new Date(selectedDate) > new Date(selectedDate2)) {
      toast.warning("Start date cannot be after end date");
      return;
    }
    setSelectedOption("CustomizeDate");
    fetchOrderDetails(1, paginationData.pageSize, searchTerm, "CustomizeDate");
  }, [selectedDate, selectedDate2, fetchOrderDetails, paginationData.pageSize, searchTerm]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPaginationData((prev) => ({
        ...prev,
        currentPage: newPage + 1,
      }));

      fetchOrderDetails(newPage + 1, paginationData.pageSize, searchTerm, selectedOption);
    },
    [fetchOrderDetails, paginationData.pageSize, searchTerm, selectedOption],
  );

  const handleRowsPerPageChange = useCallback(
    (newRowsPerPage: number) => {
      setPaginationData((prev) => ({
        ...prev,
        pageSize: newRowsPerPage,
        currentPage: 1,
      }));

      fetchOrderDetails(1, newRowsPerPage, searchTerm, selectedOption);
    },
    [fetchOrderDetails, searchTerm, selectedOption],
  );

  const handleImport = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      await api.post(`/app/v2/import_export`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Orders imported successfully.");
      fetchOrderDetails(paginationData.currentPage, paginationData.pageSize, searchTerm, selectedOption);
    } catch (error: any) {
      console.error("Error importing file:", error);
      toast.error("Failed to import file.");
      throw error;
    }
  };

  const handleDownload = async (format: "CSV" | "PDF", orders: Order[]) => {
    if (!orders.length) {
      toast.warn("No orders selected to export.");
      return false;
    }

    try {
      setLoading(true);

      // 🔗 Build query params using current state
      const queryParams = new URLSearchParams({
        searchBy: searchTerm || "",
        pageNumber: String(paginationData.currentPage),
        pageSize: String(paginationData.pageSize),
        dateFilter: selectedOption === "CustomizeDate" ? "custom" : selectedOption.toLowerCase(),
      });

      if (selectedOption === "CustomizeDate") {
        queryParams.append("startDate", selectedDate);
        queryParams.append("endDate", selectedDate2);
      }

      // 🔥 Build API endpoint based on format
      const endpoint = format === "CSV" ? `app/v2/import_export?export=csv&${queryParams.toString()}` : `app/v2/import_export?export=pdf&${queryParams.toString()}`;

      const response = await api.get(endpoint);

      if ((response.status === 200 || response.status === 201) && response.data?.message) {
        toast.success(`${format} export email sent successfully.`);
        return true;
      } else {
        toast.error(`Failed to send ${format} export email.`);
        return false;
      }
    } catch (error) {
      console.error(`Error exporting ${format}:`, error);
      toast.error(`Error exporting ${format}. Please try again.`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userConfigration?.tableDefaultSize) return;

    const newSize = userConfigration.tableDefaultSize;

    setPaginationData((prev) => ({
      ...prev,
      pageSize: newSize,
      currentPage: 1,
    }));

    fetchOrderDetails(1, newSize, searchTerm, selectedOption);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userConfigration?.tableDefaultSize]);

  return {
    orderList,
    loading,
    error,
    paginationData,
    searchTerm,
    selectedOption,
    selectedDate,
    selectedDate2,
    fetchOrderDetails,
    handleSearch,
    handleFilterChange,
    applyCustomDateFilter,
    setSelectedDate,
    setSelectedDate2,
    handlePageChange,
    handleRowsPerPageChange,
    handleImport,
    handleDownload,
    setOrderList,
  };
};
